import { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, User, ChevronDown } from 'lucide-react'
import { Sidebar } from './Sidebar'

const PAGE_TITLES: Record<string, string> = {
  '/':         'Context Lake',
  '/mcp-registry': 'MCP Registry',
  '/catalog':  'AI Catalog',
  '/projects': 'My Projects',
  '/profile':  'Profile & MCP Token',
  '/admin':    'Admin',
}

interface AuthUser {
  email: string
  name: string
}

function UserAvatar({ name, email }: Readonly<{ name: string; email: string }>) {
  const initials = name
    ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : email[0].toUpperCase()

  return (
    <div
      title={`${name}\n${email}`}
      style={{
        width: 28, height: 28, borderRadius: '50%',
        background: 'var(--brand)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 600, letterSpacing: '-0.3px',
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  )
}

function UserMenu({ user }: Readonly<{ user: AuthUser }>) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '4px 8px', background: 'transparent',
          border: '1px solid transparent', borderRadius: 'var(--radius-xs)',
          cursor: 'pointer', transition: 'all var(--transition-fast)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--surface-hover)'
          e.currentTarget.style.borderColor = 'var(--border)'
        }}
        onMouseLeave={e => {
          if (!open) {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.borderColor = 'transparent'
          }
        }}
      >
        <UserAvatar name={user.name} email={user.email} />
        <span style={{ fontSize: 'var(--text-body3-size)', color: 'var(--text-primary)', fontWeight: 500 }}>
          {user.name || user.email}
        </span>
        <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-s)', boxShadow: 'var(--shadow-l)',
          minWidth: 220, padding: 'var(--space-1)', zIndex: 100,
        }}>
          {/* User info section */}
          <div style={{
            padding: '12px var(--space-2)', borderBottom: '1px solid var(--border)',
            marginBottom: 'var(--space-1)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UserAvatar name={user.name} email={user.email} />
              <div>
                <div style={{ fontSize: 'var(--text-body2-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {user.name || 'User'}
                </div>
                <div style={{ fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)', marginTop: 1 }}>
                  {user.email}
                </div>
              </div>
            </div>
          </div>

          {/* Profile item */}
          <button
            onClick={() => {
              navigate('/profile')
              setOpen(false)
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '8px var(--space-2)', background: 'transparent',
              border: 'none', borderRadius: 'var(--radius-xs)',
              fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)',
              cursor: 'pointer', transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            <User size={14} />
            <span>View Profile & Token</span>
          </button>

          {/* Sign out */}
          <button
            onClick={() => {
              const apiUrl = import.meta.env.VITE_API_URL ?? ''
              const redirectUri = globalThis.location.origin
              globalThis.location.href = `${apiUrl}/api/v1/auth/logout?redirect_uri=${encodeURIComponent(redirectUri)}`
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '8px var(--space-2)', background: 'transparent',
              border: 'none', borderRadius: 'var(--radius-xs)',
              fontSize: 'var(--text-body3-size)', color: 'var(--error)',
              cursor: 'pointer', transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,50,50,0.06)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

export function AppShell() {
  const { pathname } = useLocation()
  const title = PAGE_TITLES[pathname] ?? 'Context Lake'
  const [user, setUser] = useState<AuthUser | null>(null)

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL ?? ''
    fetch(`${apiUrl}/api/v1/auth/me`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setUser(data) })
      .catch(() => {})
  }, [])

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <header style={{
          height: 56, padding: '0 var(--space-4)', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: '1px solid var(--border)',
          background: 'var(--surface)', flexShrink: 0,
        }}>
          <h1 style={{ fontSize: 'var(--text-h7-size)', fontWeight: 500, letterSpacing: '-0.5px', margin: 0 }}>
            {title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)' }}>
              KONE AI Context Lake
            </span>
            {user ? (
              <UserMenu user={user} />
            ) : (
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'var(--surface-active)', animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            )}
          </div>
        </header>

        <main style={{ flex: 1, padding: 'var(--space-4)', overflow: 'auto' }}>
          <Outlet />
        </main>
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  )
}
